import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, Lock, CheckCircle2, ArrowLeft, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TEST_PACKAGES } from "@/data/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registrationId = searchParams.get('registration_id');
  
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (registrationId) {
      fetchRegistration();
    } else {
      toast.error('No registration found');
      navigate('/');
    }
  }, [registrationId]);

  const fetchRegistration = async () => {
    try {
      const response = await axios.get(`${API}/registrations/${registrationId}`);
      setRegistration(response.data);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load registration');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        registration_id: registrationId,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to initiate payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-30 py-4 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-[#1E3A8A] rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div className="logo-text text-xl">
              English<span className="logo-accent">Test</span>Solution
            </div>
          </div>
        </div>
      </header>

      <main className="py-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            className="mb-6 text-slate-600"
            onClick={() => navigate('/')}
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Order Summary */}
            <Card className="shadow-lg">
              <CardHeader className="border-b">
                <CardTitle className="text-xl">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Student Name</p>
                    <p className="font-medium">{registration?.first_name} {registration?.last_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Test Type</p>
                    <p className="font-medium">{registration?.test_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium">{registration?.email}</p>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-3">
                  <p className="font-medium text-slate-700">Selected Packages:</p>
                  {registration?.selected_packages?.map((pkg, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-slate-600">
                        {TEST_PACKAGES[pkg]?.name || pkg}
                      </span>
                      <span className="font-medium">
                        C${((TEST_PACKAGES[pkg]?.price || 0) + (TEST_PACKAGES[pkg]?.tax || 0)).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">Total</span>
                    <span className="text-2xl font-bold text-[#1E3A8A]">
                      C${registration?.total_amount?.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Includes all applicable taxes</p>
                </div>
              </CardContent>
            </Card>

            {/* Payment Section */}
            <Card className="shadow-lg">
              <CardHeader className="border-b">
                <CardTitle className="text-xl flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Secure Payment
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <p className="text-sm text-slate-700">
                    You will be redirected to our secure payment partner <strong>Stripe</strong> to complete your payment.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span>Credit & Debit cards accepted</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span>256-bit SSL encryption</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span>PCI DSS compliant</span>
                  </div>
                </div>

                <Button 
                  className="w-full btn-primary h-12 text-lg"
                  onClick={handlePayment}
                  disabled={processing}
                  data-testid="pay-now-btn"
                >
                  {processing ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Lock className="w-5 h-5" />
                      Pay C${registration?.total_amount?.toFixed(2)}
                    </span>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-4 pt-4">
                  <div className="flex items-center gap-1 text-sm text-slate-500">
                    <ShieldCheck className="w-4 h-4 text-green-500" />
                    Secure Payment
                  </div>
                  <div className="flex items-center gap-1 text-sm text-slate-500">
                    <Lock className="w-4 h-4 text-green-500" />
                    Encrypted
                  </div>
                </div>

                {/* Refund Notice */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 text-[#F97316] font-semibold text-sm">
                    <ShieldCheck className="w-4 h-4" />
                    Refund Guarantee
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    Test Prep Package is refundable if you don't pass (minus C$50 admin fee)
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
